import { useEffect, useId, useState } from "react";
import clsx from "clsx";
import {
  canonicalPeriod,
  durationLabel,
  matchPreset,
  PERIOD_UNIT_LABELS,
  type Period,
  type PeriodPreset,
  type PeriodUnit,
} from "../lib/contractPeriod";

export interface PeriodPickerProps {
  label: string;
  hint?: string;
  presets: readonly PeriodPreset[];
  /** Erlaubte Einheiten im Modus „Anders“. */
  units: readonly PeriodUnit[];
  value: Period | null;
  onChange: (next: Period | null) => void;
  disabled?: boolean;
  required?: boolean;
  /** Kurzer Vorschautext, z. B. „jährlich“ – zeigt die normalisierte Bedeutung der Eingabe. */
  preview?: string;
}

/**
 * Zeitraum wählen, ohne dass dieselbe Angabe zweimal auftaucht: Die üblichen
 * Werte stehen als Chips bereit („Jährlich“ statt „12 Monate“ und „1 Jahr“);
 * alles andere geht über „Anders“ mit Zahl + Einheit. Entspricht eine freie
 * Eingabe einem Chip (z. B. 12 Monate), springt die Auswahl automatisch auf
 * den Chip.
 */
export function PeriodPicker({
  label,
  hint,
  presets,
  units,
  value,
  onChange,
  disabled = false,
  required = false,
  preview,
}: PeriodPickerProps) {
  const id = useId();
  const matched = matchPreset(presets, value);
  // „Anders“ bleibt offen, solange der Benutzer tippt – auch wenn eine
  // Zwischeneingabe (z. B. „1 Monat“ auf dem Weg zu „18 Monate“) einem Chip entspricht.
  const [customOpen, setCustomOpen] = useState(() => Boolean(value) && !matched);
  const [draftValue, setDraftValue] = useState(value && !matched ? String(value.value) : "");
  const [draftUnit, setDraftUnit] = useState<PeriodUnit>(
    value && !matched ? value.unit : (units.includes("months") ? "months" : units[units.length - 1] ?? "months"),
  );

  // Von außen gesetzter Wert ohne passenden Chip (z. B. nach dem Laden): Freitext öffnen und vorbelegen.
  useEffect(() => {
    if (value && !matched) {
      setCustomOpen(true);
      setDraftValue(String(value.value));
      setDraftUnit(value.unit);
    }
  }, [value?.value, value?.unit]); // eslint-disable-line react-hooks/exhaustive-deps

  function commitCustom(nextValue: string, nextUnit: PeriodUnit) {
    setDraftValue(nextValue);
    setDraftUnit(nextUnit);
    const n = Number(nextValue);
    if (!nextValue.trim() || !Number.isFinite(n) || n <= 0) {
      onChange(null);
      return;
    }
    // Rohwert durchreichen; die kanonische Form (12 Monate → 1 Jahr) entsteht beim Speichern.
    onChange({ value: Math.round(n), unit: nextUnit });
  }

  const customPreview = customOpen && value ? (preview ?? durationLabel(canonicalPeriod(value, units))) : "";

  return (
    <fieldset className="min-w-0" disabled={disabled} aria-describedby={hint ? `${id}-hint` : undefined}>
      <legend className="eh-label text-xs">
        {label}
        {required ? <span className="text-status-red"> *</span> : null}
      </legend>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {presets.map((preset) => {
          const active = matched?.key === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              aria-pressed={active}
              className={clsx(
                "rounded-full px-3 py-1 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
                active
                  ? "bg-accent text-label-on-accent"
                  : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
              )}
              onClick={() => {
                setCustomOpen(false);
                onChange(preset.period);
              }}
            >
              {preset.label}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={customOpen}
          className={clsx(
            "rounded-full px-3 py-1 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
            customOpen && !matched
              ? "bg-accent text-label-on-accent"
              : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
          )}
          onClick={() => setCustomOpen(true)}
        >
          Anders …
        </button>
        {!required && value ? (
          <button
            type="button"
            className="rounded-full px-2 py-1 text-xs font-medium text-label-tertiary hover:text-label"
            onClick={() => {
              setCustomOpen(false);
              setDraftValue("");
              onChange(null);
            }}
          >
            Zurücksetzen
          </button>
        ) : null}
      </div>
      {customOpen ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-sm text-label-secondary">alle</span>
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            className="eh-input w-24"
            aria-label={`${label}: Anzahl`}
            value={draftValue}
            onChange={(e) => commitCustom(e.target.value, draftUnit)}
          />
          <select
            className="eh-input w-32"
            aria-label={`${label}: Einheit`}
            value={draftUnit}
            onChange={(e) => commitCustom(draftValue, e.target.value as PeriodUnit)}
          >
            {units.map((unit) => (
              <option key={unit} value={unit}>
                {PERIOD_UNIT_LABELS[unit].many}
              </option>
            ))}
          </select>
          {customPreview ? (
            <span className="text-sm text-label-tertiary">
              = {customPreview}
              {matched ? ` (entspricht „${matched.label}“)` : ""}
            </span>
          ) : null}
        </div>
      ) : null}
      {hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-label-secondary">
          {hint}
        </p>
      ) : null}
    </fieldset>
  );
}
